import React, { useState, useContext, useEffect } from 'react';
import Flashcard from './Flashcard';
import ProgressNavigator from './ProgressNavigator';
import Summary from './Summary';
import PaywallComponent from '../common/PaywallComponent';
import { AuthContext } from '../../context/AuthContext';
import { getQuestionLimit } from '../../services/userAPIService';

const QuizEngine = ({ questions, onComplete }) => {
  const { hasSubscription } = useContext(AuthContext);
  const [showPaywall, setShowPaywall] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [quizQuestions, setQuizQuestions] = useState(questions.map(q => ({
    ...q,
    result: 'not answered',
    originalStreak: q.streak || 0,
    originalCorrectCount: q.correct_count || 0,
    originalIncorrectCount: q.incorrect_count || 0
  })));

  useEffect(() => {
    const checkAccess = async () => {
      if (!hasSubscription) {
        try {
          const limitData = await getQuestionLimit();
          if (limitData.questions_answered_total >= limitData.total_limit) {
            setShowPaywall(true);
          }
        } catch (error) {
          console.error('Error checking question limit:', error);
        }
      }
      setIsLoading(false);
    };

    checkAccess();
  }, [hasSubscription]);

  const handleAnswer = (isCorrect) => {
    console.log('Handling answer:', isCorrect);
    console.log('Current question before update:', quizQuestions[currentQuestionIndex]);
    
    const updatedQuestions = [...quizQuestions];
    const currentQuestion = updatedQuestions[currentQuestionIndex];

    const newStreak = isCorrect ? (currentQuestion.originalStreak + 1) : 0;
    const newCorrectCount = isCorrect ? (currentQuestion.originalCorrectCount + 1) : currentQuestion.originalCorrectCount;
    const newIncorrectCount = !isCorrect ? (currentQuestion.originalIncorrectCount + 1) : currentQuestion.originalIncorrectCount;

    updatedQuestions[currentQuestionIndex] = {
      ...currentQuestion,
      result: isCorrect ? 'Correct' : 'Incorrect',
      streak: newStreak,
      correct_count: newCorrectCount,
      incorrect_count: newIncorrectCount
    };

    console.log('Updated question:', updatedQuestions[currentQuestionIndex]);
    setQuizQuestions(updatedQuestions);
    setCurrentQuestionIndex(prev => prev + 1);
  };

  const handleNext = () => {
    if (currentQuestionIndex < quizQuestions.length) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleRestart = () => {
    setCurrentQuestionIndex(0);
    setQuizQuestions(questions.map(q => ({
      ...q,
      result: 'not answered',
      originalStreak: q.streak || 0,
      originalCorrectCount: q.correct_count || 0,
      originalIncorrectCount: q.incorrect_count || 0
    })));
  };

  const handleComplete = async () => {
    if (onComplete) {
      console.log('Completing quiz with questions:', quizQuestions);
      
      // Entferne die "original" Felder und behalte nur die aktualisierten Werte
      const completedQuestions = quizQuestions.map(q => {
        const { originalStreak, originalCorrectCount, originalIncorrectCount, ...rest } = q;
        return rest;
      });
      
      console.log('Sending completed questions to backend:', completedQuestions);
      await onComplete(completedQuestions);
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 text-center">
        <span className="loading loading-spinner loading-lg"></span>
        <p className="mt-2">Lade Fragen...</p>
      </div>
    );
  }

  const isSummaryView = currentQuestionIndex >= quizQuestions.length;

  return (
    <>
      {showPaywall ? (
        <PaywallComponent />
      ) : (
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body p-0">
            {!isSummaryView ? (
              <Flashcard
                question={quizQuestions[currentQuestionIndex]}
                onAnswer={handleAnswer}
              />
            ) : (
              <Summary 
                questions={quizQuestions} 
                onRestart={handleRestart}
                onComplete={handleComplete}
              />
            )}
            <ProgressNavigator
              current={currentQuestionIndex + 1}
              total={quizQuestions.length + 1}
              onNext={handleNext}
              onPrevious={handlePrevious}
              onComplete={handleComplete}
              showSummaryControls={isSummaryView}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default QuizEngine; 